import { Message, MessageContent } from '../types/chat';
import { EventBus } from '../utils/eventBus';
import { renderMarkdown } from '../utils/markdown';
import { clearElement } from '../utils/dom';

export class MessageList {
  private container: HTMLElement | null = null;
  private messages: Message[] = [];
  private messageElements: Map<string, HTMLElement> = new Map();

  constructor(private eventBus: EventBus) {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventBus.on('conversation:updated', (conversation) => {
      this.updateMessages(conversation.messages);
    });

    this.eventBus.on('activeConversation:changed', () => {
      this.messages = [];
      this.messageElements.clear();
      this.render();
    });
  }

  mount(container: HTMLElement): void {
    this.container = container;
    this.render();
  }

  private updateMessages(newMessages: Message[]): void {
    if (!this.container) return;

    const oldMessages = this.messages;
    this.messages = newMessages;

    // Handle empty state
    if (newMessages.length === 0) {
      this.messageElements.clear();
      this.render();
      return;
    }

    // Remove empty state if present
    const emptyState = this.container.querySelector('.empty-state');
    if (emptyState) {
      emptyState.remove();
    }

    // Find new messages to add
    const oldIds = new Set(oldMessages.map(m => m.id));
    const newIds = new Set(newMessages.map(m => m.id));

    // Remove deleted messages
    for (const oldMsg of oldMessages) {
      if (!newIds.has(oldMsg.id)) {
        const el = this.messageElements.get(oldMsg.id);
        if (el) {
          el.remove();
          this.messageElements.delete(oldMsg.id);
        }
      }
    }

    // Add new messages or update existing ones
    for (const msg of newMessages) {
      if (!oldIds.has(msg.id)) {
        // New message - append
        const messageEl = this.renderMessage(msg);
        this.container.appendChild(messageEl);
        this.messageElements.set(msg.id, messageEl);
      } else {
        // Existing message - check if content changed
        const oldMsg = oldMessages.find(m => m.id === msg.id);
        if (oldMsg && this.getContentString(oldMsg.content) !== this.getContentString(msg.content)) {
          // Update content only
          const el = this.messageElements.get(msg.id);
          if (el) {
            const contentEl = el.querySelector('.message-content');
            if (contentEl) {
              this.updateMessageContent(contentEl as HTMLElement, msg.content);
            }
          }
        }
      }
    }

    this.scrollToBottom();
  }

  private getContentString(content: string | MessageContent[]): string {
    if (typeof content === 'string') return content;
    return JSON.stringify(content);
  }

  private updateMessageContent(contentEl: HTMLElement, content: string | MessageContent[]): void {
    if (typeof content === 'string') {
      contentEl.innerHTML = renderMarkdown(content);
    } else {
      clearElement(contentEl);
      content.forEach((item: MessageContent) => {
        if (item.type === 'text' && item.text) {
          const textDiv = document.createElement('div');
          textDiv.innerHTML = renderMarkdown(item.text);
          contentEl.appendChild(textDiv);
        } else if (item.type === 'image_url' && item.image_url) {
          const img = document.createElement('img');
          img.src = item.image_url.url;
          img.className = 'message-image';
          img.alt = 'Uploaded image';
          contentEl.appendChild(img);
        }
      });
    }
  }

  private render(): void {
    if (!this.container) return;

    clearElement(this.container);
    this.messageElements.clear();

    if (this.messages.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty-state';
      empty.textContent = 'Start a conversation...';
      this.container.appendChild(empty);
      return;
    }

    this.messages.forEach(message => {
      const messageEl = this.renderMessage(message);
      this.container!.appendChild(messageEl);
      this.messageElements.set(message.id, messageEl);
    });

    this.scrollToBottom();
  }

  private renderMessage(message: Message): HTMLElement {
    const messageEl = document.createElement('div');
    messageEl.className = `message message-${message.role}`;

    const header = document.createElement('div');
    header.className = 'message-header';
    header.textContent = message.role === 'user' ? 'You' : 'Assistant';
    messageEl.appendChild(header);

    const content = document.createElement('div');
    content.className = 'message-content';

    if (typeof message.content === 'string') {
      content.innerHTML = renderMarkdown(message.content);
    } else {
      // Handle mixed content (text + images)
      message.content.forEach((item: MessageContent) => {
        if (item.type === 'text' && item.text) {
          const textDiv = document.createElement('div');
          textDiv.innerHTML = renderMarkdown(item.text);
          content.appendChild(textDiv);
        } else if (item.type === 'image_url' && item.image_url) {
          const img = document.createElement('img');
          img.src = item.image_url.url;
          img.className = 'message-image';
          img.alt = 'Uploaded image';
          content.appendChild(img);
        }
      });
    }

    messageEl.appendChild(content);

    const timestamp = document.createElement('div');
    timestamp.className = 'message-timestamp';
    timestamp.textContent = new Date(message.timestamp).toLocaleTimeString();
    messageEl.appendChild(timestamp);

    return messageEl;
  }

  private scrollToBottom(): void {
    if (this.container) {
      this.container.scrollTop = this.container.scrollHeight;
    }
  }

  destroy(): void {
    this.eventBus.off('conversation:updated');
    this.eventBus.off('activeConversation:changed');
  }
}
